import type { AnalyticsEventName, AnalyticsProps } from './analyticsEvents';

/**
 * Очередь событий — чистая часть аналитики (FN-1.2).
 *
 * Вынесена из рантайма, потому что здесь живут все решения, которые можно принять неправильно и не
 * заметить: что делать с переполнением, сколько отправлять за раз, что удалять после успеха. В
 * `analytics.ts` остались только таймеры, хранилище и сеть.
 */
export type QueuedAnalyticsEvent = {
  /** Первичный ключ на сервере: повтор пачки гасится по нему, а не отдельной дедупликацией. */
  id: string;
  sessionId: string;
  name: AnalyticsEventName;
  occurredAtMs: number;
  props?: AnalyticsProps;
};

/**
 * Потолок очереди. Достигается только когда сети нет долго; дальше очередь перестаёт расти, а не
 * съедает хранилище устройства.
 */
export const MAX_QUEUED_EVENTS = 500;

/** Столько же принимает сервер за один запрос. */
const MAX_BATCH_SIZE = 100;

/**
 * Добавить событие, вытеснив самые старые при переполнении.
 *
 * Выбрасываем старое, а не отказываемся принимать новое: свежие события ближе к тому, что человек
 * делает сейчас, и именно они нужны, когда связь наконец появится.
 */
export function appendEvent(
  queue: readonly QueuedAnalyticsEvent[],
  event: QueuedAnalyticsEvent,
  cap: number = MAX_QUEUED_EVENTS,
): QueuedAnalyticsEvent[] {
  const next = [...queue, event];
  return next.length <= cap ? next : next.slice(next.length - cap);
}

/** Первые события очереди — отправляем в том порядке, в каком они случились. */
export function takeBatch(
  queue: readonly QueuedAnalyticsEvent[],
  size: number = MAX_BATCH_SIZE,
): QueuedAnalyticsEvent[] {
  return queue.slice(0, Math.max(0, size));
}

/**
 * Убрать отправленное.
 *
 * По идентификаторам, а не по длине: пока пачка была в сети, `track()` мог добавить в очередь новые
 * события, и срез по количеству удалил бы не то, что уехало.
 */
export function dropSent(
  queue: readonly QueuedAnalyticsEvent[],
  sent: readonly QueuedAnalyticsEvent[],
): QueuedAnalyticsEvent[] {
  if (sent.length === 0) {
    return [...queue];
  }
  const sentIds = new Set(sent.map((e) => e.id));
  return queue.filter((e) => !sentIds.has(e.id));
}

/**
 * Разобрать очередь, пережившую перезапуск.
 *
 * Всё, что не похоже на событие, выбрасывается поштучно: одна битая строка в хранилище не должна
 * стоить всей накопленной очереди, а исключение при старте — тем более.
 */
export function parseStoredQueue(raw: string | null | undefined): QueuedAnalyticsEvent[] {
  if (!raw) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(isQueuedEvent).slice(-MAX_QUEUED_EVENTS);
  } catch {
    return [];
  }
}

function isQueuedEvent(value: unknown): value is QueuedAnalyticsEvent {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const e = value as Record<string, unknown>;
  return (
    typeof e.id === 'string' &&
    e.id.length > 0 &&
    typeof e.sessionId === 'string' &&
    e.sessionId.length > 0 &&
    typeof e.name === 'string' &&
    e.name.length > 0 &&
    typeof e.occurredAtMs === 'number' &&
    Number.isFinite(e.occurredAtMs)
  );
}
